import React, {useState, useEffect, useMemo} from 'react'
import {
  accordionWrapStyle,
  accordionHeaderStyle,
  categoryLabelStyle,
  chevronStyle,
} from './subscription-summary.styles'
import {ParagraphText} from '@thryvlabs/maverick'
import {
  commandCenterIcon,
  marketingCenterIcon,
  reportingCenterIcon,
  workforceCenterIcon,
  businessCenterIcon,
  keapMaxIcon,
  thryvGrowthPlatformIcon,
  // promoCodeIcon,
} from '../assets'
import {formatPrice} from '../utils/currency-format'
import {useSelector} from 'react-redux'
import downArrowIcon from '../assets/icons/downArrow.svg'
import onboardingFeeIcon from '../assets/onboarding-fee-icon.svg'

const DEFAULT_CART_ITEMS = [
  {
    name: 'my account',
    icon: commandCenterIcon,
  },
  {
    name: 'business center',
    icon: businessCenterIcon,
  },
  {
    name: 'marketing center',
    icon: marketingCenterIcon,
  },
  {
    name: 'reporting center',
    icon: reportingCenterIcon,
  },
  {
    name: 'workforce center',
    icon: workforceCenterIcon,
  },
  {
    name: 'keap max',
    icon: keapMaxIcon,
  },
  {
    name: 'keap',
    icon: keapMaxIcon,
  },
  {
    name: 'onboarding fee',
    icon: onboardingFeeIcon,
  },
  {
    name: 'website design fee',
    icon: onboardingFeeIcon,
  },
  {
    name: 'starter',
    icon: thryvGrowthPlatformIcon,
  },
  {
    name: 'signature',
    icon: thryvGrowthPlatformIcon,
  },
  {
    name: 'premier',
    icon: thryvGrowthPlatformIcon,
  },
  {
    name: 'thryv growth platform',
    icon: thryvGrowthPlatformIcon,
  },
]

const KCP_AU_QTY_MAPPING = [
  {amount: 418, qty: 380},
  {amount: 704, qty: 640},
  {amount: 1001, qty: 910},
  {amount: 2002, qty: 1820},
]

const NON_ADDED_PLANS = ['addon-5-signatures_usage', 'app-websitebuilder']

const SEO_ADDON_CODES = ['addon-5-seopb-m2m', 'addon-5-seokey-m2m']

const SEO_GST_CODES = new Set([
  'seobase-5-thryv-m2m',
  'addon-5-seopb-m2m',
  'addon-5-seokey-m2m',
])

// Sum of what the backend says each location is charged today (prorated, with tax)
// when every location has a preview (see attachLocationPreviews); otherwise null.
// Same meaning as a whole-order paymentPreview: a tax-inclusive total plus its tax.
export const getLocationPreviewSummary = cartDetailObj => {
  const locations = cartDetailObj?.locations ?? []
  if (
    locations.length < 2 ||
    !locations.every(location =>
      Number.isFinite(Number(location.preview?.total)),
    )
  ) {
    return null
  }
  const sum = key =>
    Math.round(
      locations.reduce(
        (total, location) => total + Number(location.preview?.[key] || 0),
        0,
      ) * 100,
    ) / 100
  return {total: sum('total'), tax: sum('tax')}
}

export function computeTabTotal({
  paymentDetailType,
  cartDetailObj,
  previewDetails,
  finalBillingAmountDetails,
  couponList = [],
  country = 'US',
}) {
  const isAu = country === 'AU'
  const gstFor = code => (isAu && SEO_GST_CODES.has(code) ? 1.1 : 1)

  if (paymentDetailType === 'monthly' || paymentDetailType === 'annual') {
    const isAnnualType = paymentDetailType === 'annual'
    const plans = cartDetailObj?.plans ?? []
    const oneTimeFeeItemCodes = (
      cartDetailObj?.productSub?.oneTimeFees || []
    ).map(f => f.code)
    const isOTF = item =>
      item.prodType === 'OneTimeFees' ||
      item.prodType === 'oneTimeFee' ||
      item.section === 'oneTimeFees' ||
      item?.groupKey === 'oneTimeFees' ||
      oneTimeFeeItemCodes.includes(item.code)
    const isAnnual = item => {
      const fields = [
        item?.code,
        item?.planCode,
        item?.addonCode,
        item?.parentProduct,
        item?.name,
        item?.displayName,
        item?.productName,
        item?.billingCycle,
        item?.billing_cycle,
        item?.intervalUnit,
        item?.interval_unit,
      ]
      return fields.some(v => {
        const n = String(v || '').toLowerCase()
        return n.includes('annual') || n.includes('yearly') || n === 'year'
      })
    }
    const itemAmount = item => {
      const qty = item?.quantity || 1
      const multiplier = gstFor(item?.code)
      if (Number.isFinite(Number(item?.unitAmount)))
        return Number(item.unitAmount) * qty * multiplier
      if (Number.isFinite(Number(item?.amount)))
        return Number(item.amount) * qty * multiplier
      return 0
    }
    const planCodes = new Set(plans.map(p => p.code).filter(Boolean))
    const total = plans
      .filter(
        item =>
          !isOTF(item) && (isAnnualType ? isAnnual(item) : !isAnnual(item)),
      )
      .reduce((sum, item) => {
        let planTotal = itemAmount(item)
        ;(item?.addons || []).forEach(addon => {
          if (!planCodes.has(addon.code)) {
            planTotal += itemAmount(addon)
          }
        })
        return sum + planTotal
      }, 0)
    return Math.max(0, total)
  }
  // today
  if (finalBillingAmountDetails?.total != null) {
    return Math.max(0, finalBillingAmountDetails.total)
  }
  // Every location priced by the backend: its prorated, discounted charge is what
  // will be billed, so it replaces the catalog estimate (a whole-order backend
  // preview still takes precedence, below).
  const locationPreview = getLocationPreviewSummary(cartDetailObj)
  if (locationPreview && previewDetails?.paymentPreview?.total == null) {
    return Math.max(0, locationPreview.total)
  }
  // Locations carrying their own coupons: each is discounted by its own coupons
  // only (that is what its section shows), so the order total is their sum.
  // Applying every coupon to every line by plan code would let one location's
  // coupon discount another location's matching plan.
  const locations = cartDetailObj?.locations ?? []
  if (
    locations.length > 1 &&
    locations.every(location => Array.isArray(location.coupons)) &&
    previewDetails?.paymentPreview?.total == null
  ) {
    return locations.reduce(
      (sum, location) =>
        sum +
        computeTabTotal({
          paymentDetailType,
          cartDetailObj: location.cart,
          couponList: location.coupons,
          country,
        }),
      0,
    )
  }
  const plans = cartDetailObj?.plans ?? []
  const addOnCodes = (cartDetailObj?.productSub?.childSubscriptions || [])
    .filter(s => s.addons?.length > 0)
    .flatMap(s => s.addons)
    .map(a => a.code)
  const otfCodes = (cartDetailObj?.productSub?.oneTimeFees || []).map(
    f => f.code,
  )
  const cartItemCodes = [...addOnCodes, ...otfCodes]
  const cartPlanCodes = plans
    .map(p => p.code)
    .filter(c => !cartItemCodes.includes(c))
  let deduction = 0
  // Core links nest add-ons under their plan line, and rawTotal below charges
  // them, so they are discountable lines too (as in the Coupons & Discounts total);
  // skip any also listed top-level. Legacy links keep their behaviour.
  const discountableLines = cartDetailObj?.isCoreLink
    ? plans.flatMap(plan => [
        plan,
        ...(plan.addons || []).filter(
          addon => !plans.some(p => p.code === addon.code),
        ),
      ])
    : plans
  discountableLines.forEach(product => {
    // Core links only: an already-owned line is not charged today (see rawTotal
    // below), so there is nothing to discount. Legacy links keep their behaviour.
    if (cartDetailObj?.isCoreLink && product?.isAlreadyAdded) return
    let best = 0
    couponList.forEach(coupon => {
      const ok =
        (coupon.appliesToAllPlans && cartPlanCodes.includes(product.code)) ||
        (coupon.appliesToAllItems && cartItemCodes.includes(product.code)) ||
        coupon.appliesToPlans?.includes(product.code) ||
        coupon.appliesToItems?.includes(product.code)
      if (ok) {
        let d = 0
        if (coupon.type === 'fixed') {
          d = coupon.discountAmount || 0
        } else if (coupon.type === 'percent') {
          d =
            ((coupon.discountPercent || 0) *
              (product.unitAmount || 0) *
              (product.quantity || 1)) /
            100
        }
        if (d > best) best = d
      }
    })
    deduction += best
  })
  if (previewDetails?.paymentPreview?.total != null) {
    return Math.max(0, previewDetails.paymentPreview.total || 0)
  }
  const filteredPlans = plans.filter(item => !item?.isAlreadyAdded)
  const topLevelIdentifiers = new Set(
    filteredPlans.flatMap(p => [p.code, p.name].filter(Boolean)),
  )
  const rawTotal =
    plans.reduce((sum, item) => {
      if (item?.isAlreadyAdded) {
        // Parent is already subscribed — only count new (non-already-added) addons inside it
        return (
          sum +
          (item?.addons || [])
            .filter(addon => !addon?.isAlreadyAdded)
            .reduce((addonSum, addon) => {
              if (
                topLevelIdentifiers.has(addon.code) ||
                topLevelIdentifiers.has(addon.name)
              ) {
                return addonSum
              }
              const addonQty = addon?.quantity || 1
              const addonUnit = Number(addon?.unitAmount ?? addon?.amount ?? 0)
              return addonSum + addonUnit * addonQty * gstFor(addon?.code)
            }, 0)
        )
      }
      const qty = item?.quantity || 1
      const unit = Number(item?.unitAmount ?? item?.amount ?? 0)
      let itemTotal = unit * qty * gstFor(item?.code)

      ;(item?.addons || []).forEach(addon => {
        const alreadyCounted =
          (addon.code && topLevelIdentifiers.has(addon.code)) ||
          (addon.name && topLevelIdentifiers.has(addon.name))
        if (!alreadyCounted) {
          const addonQty = addon?.quantity || 1
          const addonUnit = Number(addon?.unitAmount ?? addon?.amount ?? 0)
          itemTotal += addonUnit * addonQty * gstFor(addon?.code)
        }
      })
      return sum + itemTotal
    }, 0) || cartDetailObj?.totalAmountToday
  return Math.max(0, (rawTotal ?? 0) - deduction)
}

const isAnnualItem = item => {
  const textFields = [
    item?.code,
    item?.planCode,
    item?.addonCode,
    item?.parentProduct,
    item?.name,
    item?.displayName,
    item?.productName,
    item?.billingCycle,
    item?.billing_cycle,
    item?.intervalUnit,
    item?.interval_unit,
  ]

  const hasAnnualKeyword = textFields.some(value => {
    const normalized = String(value || '').toLowerCase()
    return (
      normalized.includes('annual') ||
      normalized.includes('yearly') ||
      normalized === 'year'
    )
  })

  if (hasAnnualKeyword) return true

  const intervalLength = Number(
    item?.intervalLength ||
      item?.interval_length ||
      item?.billingInterval ||
      item?.billing_interval ||
      0,
  )

  return Number.isFinite(intervalLength) && intervalLength >= 12
}

// The "Thryv Add-Ons" / "Keap Add-Ons" wrapper plans belong under Add-Ons even
// when the catalog tags them prod_type "Center" (their child add-ons render
// nested inside them, so a Center tag dumps the whole bundle under Software).
const isAddonBundle = item => {
  const code = String(item?.code || item?.planCode || '')
  return (
    code === 'appmarket-5-thryv-m2m' || code.startsWith('addon-5-keap-addons')
  )
}

const isSoftwareItem = item =>
  item?.prodType === 'Center' && !isAddonBundle(item)

const isAddonItem = item =>
  item?.prodType === 'App' || !!item?.parentProduct || isAddonBundle(item)

// Core never lists the "Thryv Add-Ons" / "Keap Add-Ons" wrapper rows, only the
// add-ons inside them (and not the free Signature / Website Builder entitlements).
// Drop a free wrapper and lift its priced children to standalone lines so e.g.
// "Thryv Social Posts" gets its own row instead of nesting under the wrapper.
const flattenAddonBundles = items => {
  const list = items || []
  const seen = new Set(list.map(i => i?.planCode || i?.code).filter(Boolean))
  return list.flatMap(item => {
    if (!isAddonBundle(item)) return [item]
    const price = Number(
      item?.unitAmount ?? item?.amount ?? item?.subtotal ?? 0,
    )
    const children = (item?.addons || []).filter(child => {
      const code = child?.planCode || child?.code
      if (NON_ADDED_PLANS.includes(code) || seen.has(code)) return false
      seen.add(code)
      return true
    })
    const lifted = children.map(child => ({
      ...child,
      parentProduct: child?.parentProduct || item?.code,
    }))
    return price > 0 ? [{...item, addons: []}, ...lifted] : lifted
  })
}

const isOneTimeFeeCartItem = (item, cart) =>
  item.prodType === 'OneTimeFees' ||
  item.prodType === 'oneTimeFee' ||
  item.section === 'oneTimeFees' ||
  item.groupKey === 'oneTimeFees' ||
  (cart?.productSub?.oneTimeFees || []).some(f => f.code === item.code)

// Number of rows the Software / Add-Ons / One Time Fees sections actually list
// for one cart (a whole order, or a single location's slice of it) — applies
// the same visibility rules as the section renderers so a location header's
// count always matches the rows under it.
export function countTabItems(cartDetailObj, paymentDetailType) {
  const isMonthly = paymentDetailType === 'monthly'
  const isAnnual = paymentDetailType === 'annual'
  const isToday = !isMonthly && !isAnnual
  return flattenAddonBundles(cartDetailObj?.plans).filter(item => {
    if (NON_ADDED_PLANS.includes(item.code)) return false
    if (isToday && item.isAlreadyAdded) return false
    if (isOneTimeFeeCartItem(item, cartDetailObj)) return isToday
    if (isMonthly && isAnnualItem(item)) return false
    if (isAnnual && !isAnnualItem(item)) return false
    return isSoftwareItem(item) || isAddonItem(item)
  }).length
}

// The item count shown on a tab header. For a core link this is the same rule the
// per-location headers use, so every page shows the same number and a multi-location
// order's tab count is the sum of its locations. Returns null for any other cart so
// the caller keeps the count it had before.
export const getCoreTabItemCount = (cartDetailObj, paymentDetailType) =>
  cartDetailObj?.isCoreLink
    ? countTabItems(cartDetailObj, paymentDetailType)
    : null

export const KCPPaymentDetails = ({
  country,
  paymentState,
  paymentDetailType = 'monthly',
  cartDetailObj,
  taxAmount = -1,
  finalBillingAmountDetails,
  currentScreen,
  couponList = [],
  previewDetails = {},
  preselectedSubData = null,
  hideHeading = false,
  isTaxCalculated = false,
  // {[locationThryvId]: display name} for a multi-location (parent-pay) link.
  locationNames = {},
  // True when rendered as one location's section inside the multi-location
  // summary: order-level rows (coupons, Tax & Fees, GST note) are skipped here
  // and shown once under all the locations instead.
  isLocationSection = false,
  // True when this location section carries its own coupons (see
  // payment-details.js); the coupons then render inside the location instead
  // of once for the whole order.
  showLocationCoupons = false,
}) => {
  const isAu = (preselectedSubData?.country_code || country) === 'AU'
  const isNz = (preselectedSubData?.country_code || country) === 'NZ'
  const isAusNzPreview = ['AUD', 'NZD'].includes(
    previewDetails?.paymentPreview?.currency,
  )
  const isAusNz = isAu || isNz || isAusNzPreview
  const [subList, setSubList] = useState([])
  const [softwareOpen, setSoftwareOpen] = useState(true)
  const [addonsOpen, setAddonsOpen] = useState(true)
  const [otfOpen, setOtfOpen] = useState(true)
  const [incentivesOpen, setIncentivesOpen] = useState(true)
  const [openLocations, setOpenLocations] = useState({0: true})
  const {isMobileViewEnable} = useSelector(state => state.screenView)
  const locations = cartDetailObj?.locations ?? []
  const hasLocationCoupons = locations.some(l => Array.isArray(l.coupons))
  // A parent-pay link carries several locations' lines in one order. The
  // Recurly preview path (1:1 links) has no per-location breakdown, so it keeps
  // the flat layout, unless every location was previewed on its own (see
  // attachLocationPreviews): then the locations stay as sections, each headed
  // by its own previewed total, rather than collapsing into one list once
  // billing is saved.
  const hasEveryLocationPreview =
    locations.length > 1 && locations.every(location => location.preview)
  const isMultiLocation =
    !isLocationSection &&
    locations.length > 1 &&
    (!previewDetails?.paymentPreview || hasEveryLocationPreview)
  const isAus = country === 'AU' ? true : false

  const {overAllDeduction} = useMemo(() => {
    let totalDeduction = 0
    let addOns =
      cartDetailObj?.productSub?.childSubscriptions
        ?.filter(childSub => childSub.addons && childSub.addons.length > 0)
        ?.map(filteredChildSub => filteredChildSub.addons)
        ?.flat() || []
    let oneTimeFees = cartDetailObj?.productSub?.oneTimeFees || []

    let cartItems = [...addOns, ...oneTimeFees]
    cartItems = cartItems?.map(item => item.code) || []

    let cartPlans =
      cartDetailObj?.plans?.filter(
        plan => !cartItems.find(item => item === plan.code),
      ) || []
    cartPlans = cartPlans?.map(plan => plan.code) || []

    if (couponList.length > 0 && cartDetailObj) {
      // Add-ons nested under a plan line (core links nest them under their
      // wrapper plan) are discountable lines too; skip any also listed top-level.
      const lines = cartDetailObj.plans.flatMap(plan => [
        plan,
        ...(plan.addons || []).filter(
          addon => !cartDetailObj.plans.some(p => p.code === addon.code),
        ),
      ])
      lines.forEach(product => {
        // Core links only: an already-owned line is not charged today, so there is
        // nothing to discount. Legacy links keep their behaviour.
        if (cartDetailObj?.isCoreLink && product?.isAlreadyAdded) return
        let productDiscount = 0
        couponList.forEach(coupon => {
          let couponDiscount = 0
          const isApplicable =
            (coupon.appliesToAllPlans && cartPlans.includes(product.code)) ||
            (coupon.appliesToAllItems && cartItems.includes(product.code)) ||
            (coupon.appliesToPlans &&
              coupon.appliesToPlans.includes(product.code)) ||
            (coupon.appliesToItems &&
              coupon.appliesToItems.includes(product.code))

          if (isApplicable) {
            if (coupon.type === 'fixed') {
              couponDiscount = coupon.discountAmount
            } else if (coupon.type === 'percent') {
              couponDiscount =
                (coupon.discountPercent *
                  product.unitAmount *
                  product.quantity) /
                100
            }
          }

          if (couponDiscount > productDiscount) {
            productDiscount = couponDiscount
          }
        })
        totalDeduction += productDiscount
      })
    }

    return {overAllDeduction: totalDeduction}
  }, [couponList, cartDetailObj])

  useEffect(() => {
    let oneTimeFeeCodes = []
    previewDetails?.plans?.filter(item => {
      if (paymentDetailType === 'monthly' && item?.prodType === 'oneTimeFee') {
        oneTimeFeeCodes.push(item?.code)
      }
    })
    const oneTimeFeeItemCodes = (
      cartDetailObj?.productSub?.oneTimeFees || []
    ).map(item => item.code)

    let data = cartDetailObj?.plans

    if (paymentDetailType === 'monthly') {
      data = cartDetailObj?.monthlyPlans?.filter(
        item =>
          !(
            oneTimeFeeCodes.includes(item.code) ||
            item.prodType === 'OneTimeFees' ||
            item.prodType === 'oneTimeFee' ||
            item.section === 'oneTimeFees' ||
            item.groupKey === 'oneTimeFees' ||
            oneTimeFeeItemCodes.includes(item.code)
          ) && !isAnnualItem(item),
      )
    }

    data = data?.filter(item => !NON_ADDED_PLANS.includes(item.code))
    setSubList(data)
  }, [cartDetailObj, cartDetailObj?.plans, paymentDetailType])

  const cartItemDetail = (item, isPreview = false) => {
    if (item?.isAlreadyAdded && paymentDetailType !== 'monthly') {
      return <></>
    }
    if (
      item?.section &&
      item.section === 'oneTimeFees' &&
      paymentDetailType === 'monthly'
    ) {
      return <></>
    }
    if (
      item?.prodType &&
      item.prodType === 'OneTimeFees' &&
      paymentDetailType === 'monthly'
    ) {
      return <></>
    }
    if (
      item?.groupKey &&
      item.groupKey === 'oneTimeFees' &&
      paymentDetailType === 'monthly'
    ) {
      return <></>
    }
    let imageObj = DEFAULT_CART_ITEMS.find(cartItem =>
      item?.name?.toLowerCase().includes(cartItem?.name),
    )

    const amount = getItemAmount(item, isPreview)
    const selected = amount > 0

    const itemRowStyle = {
      position: 'relative',
      display: 'flex',
      alignItems: 'center',
      gap: '10px',
      background: selected ? '#FFF9F6' : 'white',
      ...(selected && {border: '1.5px solid #FF5000', overflow: 'hidden'}),
      borderRadius: selected ? '6px' : '10px',
      padding: '10px',
    }

    const subTextStyle = {
      fontFamily: 'Open Sans',
      fontWeight: 400,
      fontSize: '11px',
      margin: '0px',
      color: '#6a7181',
    }

    const discountAmountStyle = {
      fontFamily: 'Open Sans',
      fontWeight: 400,
      fontSize: '11px',
      margin: '0px',
      color: '#22c55e',
    }

    return (
      <div style={itemRowStyle} key={item?.name}>
        {selected && (
          <div
            style={{
              position: 'absolute',
              left: 0,
              top: '50%',
              transform: 'translateY(-50%)',
              width: '3px',
              height: '25px',
              background: '#FF5000',
              borderRadius: '0 10px 10px 0',
            }}
          />
        )}
        {item?.prodType === 'Center' && imageObj && (
          <img
            style={{borderRadius: '5px', flexShrink: 0}}
            src={imageObj.icon}
            alt={item?.name || ''}
            height="24px"
            width="24px"
          />
        )}
        <div style={{flex: 1, minWidth: 0}}>
          <p
            style={{
              fontFamily: 'Open Sans',
              fontWeight: selected ? 700 : 400,
              fontSize: '12px',
              margin: '0px',
              color: '#121212',
            }}
          >
            {item?.name}
          </p>
          {item?.addons?.length > 0 &&
            item.addons.map(addonItem => (
              <p key={addonItem.name} style={subTextStyle}>
                {addonItem.name}
              </p>
            ))}
        </div>
        <div style={{flexShrink: 0, textAlign: 'right'}}>
          <p
            style={{
              fontFamily: 'Open Sans',
              fontWeight: 400,
              fontSize: '12px',
              margin: '0px',
              color: '#121212',
            }}
          >
            {formatPrice({amount, country})}
          </p>
          {item?.addons?.length > 0 &&
            item.addons.map(addonItem => (
              <p key={addonItem.name} style={discountAmountStyle}>
                {isDiscountApplicableToAddon(addonItem)
                  ? formatPrice({
                      amount: getDiscountedAmount(addonItem),
                      country,
                    })
                  : formatPrice({
                      amount:
                        (addonItem?.unitAmount || 0) *
                        (addonItem?.quantity || 1) *
                        (isAu && SEO_GST_CODES.has(addonItem?.code) ? 1.1 : 1),
                      country,
                    })}
              </p>
            ))}
        </div>
      </div>
    )
  }

  const isDiscountApplicableToAddon = item => {
    for (const coupon of couponList) {
      if (coupon.appliesToItems?.includes(item.code)) return true
    }
    return false
  }

  const getItemAmount = (item, isPreview = false) => {
    let quantity = item?.quantity || 1
    if (isPreview) {
      const subtotal = Number(item?.subtotal)
      const total = Number(item?.total)
      //const tax = Number(item?.tax)
      const hasTotal = Number.isFinite(total)
      const hasSubtotal = Number.isFinite(subtotal)
      //const hasTax = Number.isFinite(tax)
      if (isAusNz && hasTotal) {
        return total
      }
      if (hasSubtotal) {
        return subtotal
      }
      if (Number.isFinite(Number(item?.amount))) {
        return Number(item.amount)
      }
    }

    if (item.code === 'keap_kcp-cert-5-fee' && isAus) {
      let matchedQty = KCP_AU_QTY_MAPPING.find(
        mapItem => Number(mapItem.qty) === Number(item.quantity),
      )
      quantity = Number(matchedQty?.amount)
    }
    const itemCode = item?.code || item?.planCode || item?.addonCode
    // Only skip GST when the item actually returned valid Recurly preview pricing
    // (subtotal/total). Monthly-tab items from cartDetailObj.plans come through
    // with isPreview=true but have no subtotal/total, so they still need 1.1.
    const hasValidPreviewData =
      isPreview &&
      (Number.isFinite(Number(item?.subtotal)) ||
        (isAusNz && Number.isFinite(Number(item?.total))))
    const gstMultiplier =
      !hasValidPreviewData && isAu && SEO_GST_CODES.has(itemCode) ? 1.1 : 1
    if (Number.isFinite(Number(item?.unitAmount))) {
      return Number(item.unitAmount) * quantity * gstMultiplier
    }
    if (Number.isFinite(Number(item?.amount))) {
      return Number(item.amount) * quantity * gstMultiplier
    }
    return 0
  }

  const getDiscountedAmount = item => {
    let couponAmount = 0
    if (item?.name === 'Onboarding Fee') {
      for (const coupon of couponList) {
        if (
          coupon?.appliesToPlans?.includes('onboarding-fee') ||
          coupon?.appliesToItems?.includes('thryvprfee')
        ) {
          if (coupon.discountAmount > 0) couponAmount += coupon.discountAmount
          else if (coupon.discountPercent > 0)
            couponAmount +=
              (item.quantity || 1) *
              item.unitAmount *
              (coupon.discountPercent / 100)
        }
      }
    } else {
      for (const coupon of couponList) {
        if (
          coupon.appliesToPlans?.includes(item.code) ||
          coupon.appliesToItems?.includes(item.code)
        ) {
          if (coupon.discountAmount > 0) couponAmount += coupon.discountAmount
          else if (coupon.discountPercent > 0)
            couponAmount +=
              (item.quantity || 1) *
              item.unitAmount *
              (coupon.discountPercent / 100)
        }
      }
    }
    if (couponAmount > (item.quantity || 1) * item.unitAmount) {
      return (item.quantity || 1) * item.unitAmount * -1
    } else return couponAmount * -1
  }

  const calculateTax = () => {
    if (paymentDetailType === 'monthly') {
      return 'To be calculated'
    }
    if (finalBillingAmountDetails?.tax != null) {
      return formatPrice({
        amount: finalBillingAmountDetails.tax,
        country,
      })
    }
    if (previewDetails?.paymentPreview?.tax > 0) {
      return formatPrice({
        amount: previewDetails.paymentPreview.tax,
        country,
      })
    }
    const locationPreviewTax = getLocationPreviewSummary(cartDetailObj)?.tax
    if (locationPreviewTax > 0) {
      return formatPrice({amount: locationPreviewTax, country})
    }
    if (!isTaxCalculated) {
      return 'To be calculated'
    }
    return formatPrice({amount: 0, country})
  }

  const renderPlanDetails = subDetails => {
    let planDetails = [
      ...(subDetails.apps || []),
      ...(subDetails.centers || []),
      ...(paymentDetailType === 'monthly' ? [] : subDetails.oneTimes || []),
      ...(paymentDetailType === 'today' ? subDetails.plans || [] : []),
    ]

    const ignoreCodeList = ['app-websitebuilder', 'addon-5-signatures_usage']
    let oneTimeFeeProds = (subDetails.oneTimes || []).map(item => item.planCode)

    if (
      paymentDetailType === 'monthly' &&
      cartDetailObj?.plans &&
      cartDetailObj.plans.length > 0
    ) {
      const oneTimeFeeItemCodes = (
        cartDetailObj?.productSub?.oneTimeFees || []
      ).map(item => item.code)
      const isOtfItem = item =>
        item.prodType === 'OneTimeFees' ||
        item.section === 'oneTimeFees' ||
        item.groupKey === 'oneTimeFees' ||
        oneTimeFeeProds?.includes(item.code) ||
        oneTimeFeeItemCodes.includes(item.code)
      const allPlans = flattenAddonBundles(cartDetailObj.plans).filter(
        item => !ignoreCodeList.includes(item.code) && !isAnnualItem(item),
      )
      const software = allPlans.filter(
        item => !isOtfItem(item) && isSoftwareItem(item),
      )
      const addons = allPlans.filter(
        item => !isOtfItem(item) && isAddonItem(item),
      )

      const getGroupTotal = items => {
        const total = items.reduce((sum, item) => {
          let itemTotal = getItemAmount(item, true)
          ;(item?.addons || []).forEach(addon => {
            itemTotal += getItemAmount(addon, true)
          })
          return sum + itemTotal
        }, 0)
        return formatPrice({amount: total, country})
      }
      const chevronSvg = (
        <img
          src={downArrowIcon}
          alt=""
          style={{width: '100%', height: '100%'}}
        />
      )

      return (
        <div style={{display: 'flex', flexDirection: 'column', gap: '8px'}}>
          {software.length > 0 && (
            <div style={accordionWrapStyle}>
              <div
                style={accordionHeaderStyle}
                onClick={() => setSoftwareOpen(v => !v)}
              >
                <div style={{display: 'flex', alignItems: 'center'}}>
                  <span style={chevronStyle(softwareOpen)}>{chevronSvg}</span>
                  <span style={categoryLabelStyle}>
                    Software{' '}
                    <span style={{fontWeight: 400, color: '#6a7181'}}>
                      - {software.length}{' '}
                      {software.length === 1 ? 'item' : 'items'}
                    </span>
                  </span>
                </div>
                <span style={categoryLabelStyle}>
                  {getGroupTotal(software)}
                </span>
              </div>
              {softwareOpen && (
                <div
                  style={{display: 'flex', flexDirection: 'column', gap: '5px'}}
                >
                  {software
                    .sort(itemComparator)
                    .map(item => cartItemDetail(item, true))}
                </div>
              )}
            </div>
          )}
          {addons.length > 0 && (
            <div style={accordionWrapStyle}>
              <div
                style={accordionHeaderStyle}
                onClick={() => setAddonsOpen(v => !v)}
              >
                <div style={{display: 'flex', alignItems: 'center'}}>
                  <span style={chevronStyle(addonsOpen)}>{chevronSvg}</span>
                  <span style={categoryLabelStyle}>
                    Add-Ons{' '}
                    <span style={{fontWeight: 400, color: '#6a7181'}}>
                      - {addons.length} {addons.length === 1 ? 'item' : 'items'}
                    </span>
                  </span>
                </div>
                <span style={categoryLabelStyle}>{getGroupTotal(addons)}</span>
              </div>
              {addonsOpen && (
                <div
                  style={{display: 'flex', flexDirection: 'column', gap: '5px'}}
                >
                  {addons
                    .sort(itemComparator)
                    .map(item => cartItemDetail(item, true))}
                </div>
              )}
            </div>
          )}
        </div>
      )
    }

    // For Due Today, render grouped accordion sections by category.
    // paymentPreview.apps is the authoritative source from Recurly — it includes all charged items
    // (new items AND quantity increases on existing addons) with correct prorated amounts and discounts.

    if (paymentDetailType === 'today') {
      const centerCodes = new Set(
        (cartDetailObj?.plans || []).filter(isSoftwareItem).map(p => p.code),
      )
      const alreadyAddedCodes = new Set(
        (cartDetailObj?.plans || [])
          .filter(p => p.isAlreadyAdded)
          .map(p => p.code)
          .filter(Boolean),
      )
      // The purchase-plan API returns addon items with planCode = parent bundle
      // (e.g. appmarket-5-thryv-m2m) and addonCode = the actual addon code.
      // Normalize these items so they carry code = addonCode, making them
      // identifiable by their own code rather than their parent's.
      const normalizePreviewItem = item =>
        item.addonCode ? {...item, code: item.addonCode} : item
      const flatPreviewItems = flattenAddonBundles([
        ...(subDetails.centers || []),
        ...(subDetails.apps || []).map(normalizePreviewItem),
      ]).filter(
        item =>
          !ignoreCodeList.includes(item.code || item.planCode) &&
          !alreadyAddedCodes.has(item.code || item.planCode),
      )
      // The preview lists the SEO add-ons as separate lines; show them nested
      // under AEO & SEO like the cart does before the preview loads. US-style
      // pricing only: AU/NZ amounts carry GST handling the nested rows don't.
      const seoParent = flatPreviewItems.find(
        item => (item.code || item.planCode) === 'seobase-5-thryv-m2m',
      )
      const seoChildren = flatPreviewItems.filter(item =>
        SEO_ADDON_CODES.includes(item.code),
      )
      const allPreviewItems =
        seoParent && seoChildren.length > 0 && !isAusNz
          ? flatPreviewItems
              .filter(item => !seoChildren.includes(item))
              .map(item =>
                item === seoParent
                  ? {
                      ...item,
                      seoGrouped: true,
                      addons: seoChildren.map(child => ({
                        ...child,
                        quantity: 1,
                        unitAmount: getItemAmount(child, true),
                      })),
                    }
                  : item,
              )
          : flatPreviewItems
      // The Recurly preview returns already-added bundle plans (e.g.
      // appmarket-5-thryv-m2m) as a single entry — their nested new addons are
      // not surfaced as top-level rows, so flattenAddonBundles finds nothing to
      // lift.  Pull any non-already-added addons directly from the cart instead.
      const previewItemCodes = new Set(
        allPreviewItems.map(p => p.code || p.planCode).filter(Boolean),
      )
      const newAddonsFromBundles = (cartDetailObj?.plans || [])
        .filter(p => p.isAlreadyAdded && isAddonBundle(p))
        .flatMap(bundle =>
          (bundle.addons || [])
            .filter(
              addon =>
                !addon.isAlreadyAdded &&
                !ignoreCodeList.includes(addon.planCode || addon.code) &&
                !previewItemCodes.has(addon.planCode || addon.code),
            )
            .map(addon => ({...addon, parentProduct: bundle.code})),
        )
      const allItems = [...allPreviewItems, ...newAddonsFromBundles]
      const software = allItems.filter(item =>
        centerCodes.has(item.code || item.planCode),
      )
      const addons = allItems.filter(
        item => !centerCodes.has(item.code || item.planCode),
      )
      const otf = (subDetails.oneTimes || []).filter(
        item => !alreadyAddedCodes.has(item.planCode || item.code),
      )

      const getGroupTotal = items => {
        const total = items.reduce((sum, item) => {
          const nestedSeo = item.seoGrouped
            ? item.addons.reduce((s, a) => s + a.unitAmount, 0)
            : 0
          return sum + getItemAmount(item, true) + nestedSeo
        }, 0)
        return formatPrice({amount: total, country})
      }

      const accordionHeaderStyle = {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        padding: '0 10px',
        cursor: 'pointer',
        gap: '5px',
      }

      const accordionWrapStyle = {
        background: '#F1F5FF',
        borderRadius: '10px',
        padding: '10px',
        display: 'flex',
        flexDirection: 'column',
        gap: '5px',
      }

      const categoryLabelStyle = {
        fontSize: '12px',
        fontWeight: 700,
        fontFamily: 'Open Sans',
        color: '#121212',
      }

      const chevronStyle = open => ({
        display: 'inline-flex',
        width: '12px',
        height: '12px',
        flexShrink: 0,
        transform: open ? 'rotate(180deg)' : 'rotate(0deg)',
        transition: 'transform 0.2s ease',
        marginLeft: '6px',
        marginRight: '8px',
      })

      return (
        <div style={{display: 'flex', flexDirection: 'column', gap: '8px'}}>
          {software.length > 0 && (
            <div style={accordionWrapStyle}>
              <div
                style={accordionHeaderStyle}
                onClick={() => setSoftwareOpen(v => !v)}
              >
                <div style={{display: 'flex', alignItems: 'center'}}>
                  <span style={chevronStyle(softwareOpen)}>
                    <svg
                      width="12"
                      height="12"
                      viewBox="0 0 12 12"
                      fill="none"
                      style={{width: '100%', height: '100%'}}
                    >
                      <path
                        d="M6.18568 8.49999C6.10703 8.50047 6.02907 8.48471 5.95626 8.45362C5.88345 8.42253 5.81722 8.37673 5.76138 8.31883L2.17575 4.57076C2.06322 4.45313 2 4.29359 2 4.12724C2 3.96088 2.06322 3.80134 2.17575 3.68371C2.28828 3.56608 2.44091 3.5 2.60005 3.5C2.75919 3.5 2.91182 3.56608 3.02435 3.68371L6.18568 6.99451L9.34701 3.68996C9.46133 3.58762 9.60839 3.53414 9.75879 3.54022C9.9092 3.54629 10.0519 3.61146 10.1583 3.72272C10.2647 3.83397 10.3271 3.9831 10.3329 4.14032C10.3387 4.29754 10.2875 4.45126 10.1896 4.57076L6.604 8.31883C6.49269 8.43424 6.34246 8.4993 6.18568 8.49999Z"
                        fill="currentColor"
                      />
                    </svg>
                  </span>
                  <span style={categoryLabelStyle}>
                    Software{' '}
                    <span style={{fontWeight: 400}}>
                      - {software.length} item{software.length !== 1 ? 's' : ''}
                    </span>
                  </span>
                </div>
                <span style={categoryLabelStyle}>
                  {getGroupTotal(software)}
                </span>
              </div>
              {softwareOpen && (
                <div
                  style={{
                    borderRadius: '6px',
                    overflow: 'hidden',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '5px',
                  }}
                >
                  {software.map(item =>
                    cartItemDetail({...item, prodType: 'Center'}, true),
                  )}
                </div>
              )}
            </div>
          )}

          {addons.length > 0 && (
            <div style={accordionWrapStyle}>
              <div
                style={accordionHeaderStyle}
                onClick={() => setAddonsOpen(v => !v)}
              >
                <div style={{display: 'flex', alignItems: 'center'}}>
                  <span style={chevronStyle(addonsOpen)}>
                    <svg
                      width="12"
                      height="12"
                      viewBox="0 0 12 12"
                      fill="none"
                      style={{width: '100%', height: '100%'}}
                    >
                      <path
                        d="M6.18568 8.49999C6.10703 8.50047 6.02907 8.48471 5.95626 8.45362C5.88345 8.42253 5.81722 8.37673 5.76138 8.31883L2.17575 4.57076C2.06322 4.45313 2 4.29359 2 4.12724C2 3.96088 2.06322 3.80134 2.17575 3.68371C2.28828 3.56608 2.44091 3.5 2.60005 3.5C2.75919 3.5 2.91182 3.56608 3.02435 3.68371L6.18568 6.99451L9.34701 3.68996C9.46133 3.58762 9.60839 3.53414 9.75879 3.54022C9.9092 3.54629 10.0519 3.61146 10.1583 3.72272C10.2647 3.83397 10.3271 3.9831 10.3329 4.14032C10.3387 4.29754 10.2875 4.45126 10.1896 4.57076L6.604 8.31883C6.49269 8.43424 6.34246 8.4993 6.18568 8.49999Z"
                        fill="currentColor"
                      />
                    </svg>
                  </span>
                  <span style={categoryLabelStyle}>
                    Add-Ons{' '}
                    <span style={{fontWeight: 400}}>
                      - {addons.length} item{addons.length !== 1 ? 's' : ''}
                    </span>
                  </span>
                </div>
                <span style={categoryLabelStyle}>{getGroupTotal(addons)}</span>
              </div>
              {addonsOpen && (
                <div
                  style={{
                    borderRadius: '6px',
                    overflow: 'hidden',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '5px',
                  }}
                >
                  {addons.map(item => cartItemDetail(item, true))}
                </div>
              )}
            </div>
          )}

          {paymentDetailType !== 'monthly' && otf.length > 0 && (
            <div style={accordionWrapStyle}>
              <div
                style={accordionHeaderStyle}
                onClick={() => setOtfOpen(v => !v)}
              >
                <div style={{display: 'flex', alignItems: 'center'}}>
                  <span style={chevronStyle(otfOpen)}>
                    <svg
                      width="12"
                      height="12"
                      viewBox="0 0 12 12"
                      fill="none"
                      style={{width: '100%', height: '100%'}}
                    >
                      <path
                        d="M6.18568 8.49999C6.10703 8.50047 6.02907 8.48471 5.95626 8.45362C5.88345 8.42253 5.81722 8.37673 5.76138 8.31883L2.17575 4.57076C2.06322 4.45313 2 4.29359 2 4.12724C2 3.96088 2.06322 3.80134 2.17575 3.68371C2.28828 3.56608 2.44091 3.5 2.60005 3.5C2.75919 3.5 2.91182 3.56608 3.02435 3.68371L6.18568 6.99451L9.34701 3.68996C9.46133 3.58762 9.60839 3.53414 9.75879 3.54022C9.9092 3.54629 10.0519 3.61146 10.1583 3.72272C10.2647 3.83397 10.3271 3.9831 10.3329 4.14032C10.3387 4.29754 10.2875 4.45126 10.1896 4.57076L6.604 8.31883C6.49269 8.43424 6.34246 8.4993 6.18568 8.49999Z"
                        fill="currentColor"
                      />
                    </svg>
                  </span>
                  <span style={categoryLabelStyle}>
                    One Time Fees{' '}
                    {otf.length > 0 && (
                      <span style={{fontWeight: 400}}>
                        - {otf.length} item{otf.length !== 1 ? 's' : ''}
                      </span>
                    )}
                  </span>
                </div>
                <span style={categoryLabelStyle}>
                  {formatPrice({
                    amount: otf.reduce((sum, item) => {
                      return sum + getItemAmount(item, true)
                    }, 0),
                    country,
                  })}
                </span>
              </div>
              {otfOpen && (
                <div
                  style={{
                    borderRadius: '6px',
                    overflow: 'hidden',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '5px',
                  }}
                >
                  {otf.map(item => cartItemDetail(item, true))}
                </div>
              )}
            </div>
          )}
        </div>
      )
    }

    return (
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          gap: '5px',
          marginTop: '8px',
        }}
      >
        {planDetails
          .sort(itemComparator)
          .map(item => cartItemDetail(item, true))}
      </div>
    )
  }

  const renderGroupedFallback = () => {
    const ignoreCodeList = ['app-websitebuilder', 'addon-5-signatures_usage']
    const otfCodes = new Set(
      (cartDetailObj?.productSub?.oneTimeFees || []).map(i => i.code),
    )
    const isOtfItem = item =>
      otfCodes.has(item.code) ||
      item.prodType === 'OneTimeFees' ||
      item.section === 'oneTimeFees' ||
      item.groupKey === 'oneTimeFees'

    const filtered = flattenAddonBundles(subList).filter(
      item =>
        !ignoreCodeList.includes(item.code) &&
        (paymentDetailType === 'monthly' || !item.isAlreadyAdded),
    )
    const software = filtered.filter(isSoftwareItem)
    const addons = filtered.filter(isAddonItem)
    const otf = filtered.filter(isOtfItem)

    const getGroupTotal = items => {
      const total = items.reduce((sum, item) => {
        let itemTotal = getItemAmount(item, false)
        ;(item?.addons || []).forEach(addon => {
          itemTotal += getItemAmount(addon, false)
        })
        return sum + itemTotal
      }, 0)
      return formatPrice({amount: total, country})
    }

    const accordionWrapStyle = {
      background: '#F1F5FF',
      borderRadius: '10px',
      padding: '10px',
      display: 'flex',
      flexDirection: 'column',
      gap: '5px',
    }
    const accordionHeaderStyle = {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      padding: '0 10px',
      cursor: 'pointer',
      gap: '5px',
    }
    const categoryLabelStyle = {
      fontSize: '12px',
      fontWeight: 700,
      fontFamily: 'Open Sans',
      color: '#121212',
    }
    const chevronStyle = open => ({
      display: 'inline-flex',
      width: '12px',
      height: '12px',
      flexShrink: 0,
      transform: open ? 'rotate(180deg)' : 'rotate(0deg)',
      transition: 'transform 0.2s ease',
      marginLeft: '6px',
      marginRight: '8px',
    })

    return (
      <div style={{display: 'flex', flexDirection: 'column', gap: '8px'}}>
        {software.length > 0 && (
          <div style={accordionWrapStyle}>
            <div
              style={accordionHeaderStyle}
              onClick={() => setSoftwareOpen(v => !v)}
            >
              <div style={{display: 'flex', alignItems: 'center'}}>
                <span style={chevronStyle(softwareOpen)}>
                  <svg
                    width="12"
                    height="12"
                    viewBox="0 0 12 12"
                    fill="none"
                    style={{width: '100%', height: '100%'}}
                  >
                    <path
                      d="M6.18568 8.49999C6.10703 8.50047 6.02907 8.48471 5.95626 8.45362C5.88345 8.42253 5.81722 8.37673 5.76138 8.31883L2.17575 4.57076C2.06322 4.45313 2 4.29359 2 4.12724C2 3.96088 2.06322 3.80134 2.17575 3.68371C2.28828 3.56608 2.44091 3.5 2.60005 3.5C2.75919 3.5 2.91182 3.56608 3.02435 3.68371L6.18568 6.99451L9.34701 3.68996C9.46133 3.58762 9.60839 3.53414 9.75879 3.54022C9.9092 3.54629 10.0519 3.61146 10.1583 3.72272C10.2647 3.83397 10.3271 3.9831 10.3329 4.14032C10.3387 4.29754 10.2875 4.45126 10.1896 4.57076L6.604 8.31883C6.49269 8.43424 6.34246 8.4993 6.18568 8.49999Z"
                      fill="currentColor"
                    />
                  </svg>
                </span>
                <span style={categoryLabelStyle}>
                  Software{' '}
                  <span style={{fontWeight: 400}}>
                    - {software.length} item{software.length !== 1 ? 's' : ''}
                  </span>
                </span>
              </div>
              <span style={categoryLabelStyle}>{getGroupTotal(software)}</span>
            </div>
            {softwareOpen && (
              <div
                style={{
                  borderRadius: '6px',
                  overflow: 'hidden',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '5px',
                }}
              >
                {software.map(item => cartItemDetail(item, false))}
              </div>
            )}
          </div>
        )}
        {addons.length > 0 && (
          <div style={accordionWrapStyle}>
            <div
              style={accordionHeaderStyle}
              onClick={() => setAddonsOpen(v => !v)}
            >
              <div style={{display: 'flex', alignItems: 'center'}}>
                <span style={chevronStyle(addonsOpen)}>
                  <svg
                    width="12"
                    height="12"
                    viewBox="0 0 12 12"
                    fill="none"
                    style={{width: '100%', height: '100%'}}
                  >
                    <path
                      d="M6.18568 8.49999C6.10703 8.50047 6.02907 8.48471 5.95626 8.45362C5.88345 8.42253 5.81722 8.37673 5.76138 8.31883L2.17575 4.57076C2.06322 4.45313 2 4.29359 2 4.12724C2 3.96088 2.06322 3.80134 2.17575 3.68371C2.28828 3.56608 2.44091 3.5 2.60005 3.5C2.75919 3.5 2.91182 3.56608 3.02435 3.68371L6.18568 6.99451L9.34701 3.68996C9.46133 3.58762 9.60839 3.53414 9.75879 3.54022C9.9092 3.54629 10.0519 3.61146 10.1583 3.72272C10.2647 3.83397 10.3271 3.9831 10.3329 4.14032C10.3387 4.29754 10.2875 4.45126 10.1896 4.57076L6.604 8.31883C6.49269 8.43424 6.34246 8.4993 6.18568 8.49999Z"
                      fill="currentColor"
                    />
                  </svg>
                </span>
                <span style={categoryLabelStyle}>
                  Add-Ons{' '}
                  <span style={{fontWeight: 400}}>
                    - {addons.length} item{addons.length !== 1 ? 's' : ''}
                  </span>
                </span>
              </div>
              <span style={categoryLabelStyle}>{getGroupTotal(addons)}</span>
            </div>
            {addonsOpen && (
              <div
                style={{
                  borderRadius: '6px',
                  overflow: 'hidden',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '5px',
                }}
              >
                {addons.map(item => cartItemDetail(item, false))}
              </div>
            )}
          </div>
        )}
        {paymentDetailType !== 'monthly' && otf.length > 0 && (
          <div style={accordionWrapStyle}>
            <div
              style={accordionHeaderStyle}
              onClick={() => setOtfOpen(v => !v)}
            >
              <div style={{display: 'flex', alignItems: 'center'}}>
                <span style={chevronStyle(otfOpen)}>
                  <svg
                    width="12"
                    height="12"
                    viewBox="0 0 12 12"
                    fill="none"
                    style={{width: '100%', height: '100%'}}
                  >
                    <path
                      d="M6.18568 8.49999C6.10703 8.50047 6.02907 8.48471 5.95626 8.45362C5.88345 8.42253 5.81722 8.37673 5.76138 8.31883L2.17575 4.57076C2.06322 4.45313 2 4.29359 2 4.12724C2 3.96088 2.06322 3.80134 2.17575 3.68371C2.28828 3.56608 2.44091 3.5 2.60005 3.5C2.75919 3.5 2.91182 3.56608 3.02435 3.68371L6.18568 6.99451L9.34701 3.68996C9.46133 3.58762 9.60839 3.53414 9.75879 3.54022C9.9092 3.54629 10.0519 3.61146 10.1583 3.72272C10.2647 3.83397 10.3271 3.9831 10.3329 4.14032C10.3387 4.29754 10.2875 4.45126 10.1896 4.57076L6.604 8.31883C6.49269 8.43424 6.34246 8.4993 6.18568 8.49999Z"
                      fill="currentColor"
                    />
                  </svg>
                </span>
                <span style={categoryLabelStyle}>
                  One Time Fees{' '}
                  {otf.length > 0 && (
                    <span style={{fontWeight: 400}}>
                      - {otf.length} item{otf.length !== 1 ? 's' : ''}
                    </span>
                  )}
                </span>
              </div>
              <span style={categoryLabelStyle}>
                {otf.length > 0 ? getGroupTotal(otf) : ''}
              </span>
            </div>
            {otfOpen && (
              <div
                style={{
                  borderRadius: '6px',
                  overflow: 'hidden',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '5px',
                }}
              >
                {otf.map(item => cartItemDetail(item, false))}
              </div>
            )}
          </div>
        )}
      </div>
    )
  }

  // Parent-pay link: one collapsible section per location (name, item count,
  // total), each rendered by this same component scoped to that location's
  // cart so the item rows, pricing and GST rules stay identical to the
  // single-location summary.
  const renderMultiLocation = () => (
    <div style={{display: 'flex', flexDirection: 'column', gap: '8px'}}>
      {locations.map((location, index) => {
        const isOpen = !!openLocations[index]
        const itemCount = countTabItems(location.cart, paymentDetailType)
        const locationCoupons = Array.isArray(location.coupons)
          ? location.coupons
          : couponList
        const total =
          paymentDetailType === 'today' &&
          Number.isFinite(Number(location.preview?.total))
            ? Math.max(0, Number(location.preview.total))
            : computeTabTotal({
                paymentDetailType,
                cartDetailObj: location.cart,
                couponList: locationCoupons,
                country,
              })
        return (
          <div
            key={location.locationThryvId}
            style={{
              background: '#FFFFFF',
              borderRadius: '10px',
              padding: '10px',
              display: 'flex',
              flexDirection: 'column',
              gap: '8px',
            }}
          >
            <div
              style={{...accordionHeaderStyle, padding: '0 4px'}}
              onClick={() =>
                setOpenLocations(prev => ({...prev, [index]: !prev[index]}))
              }
              role="button"
              aria-expanded={isOpen}
            >
              <div style={{display: 'flex', alignItems: 'center', minWidth: 0}}>
                <span style={chevronStyle(isOpen)}>
                  <img
                    src={downArrowIcon}
                    alt=""
                    style={{width: '100%', height: '100%'}}
                  />
                </span>
                <span style={{...categoryLabelStyle, fontSize: '13px'}}>
                  {locationNames?.[location.locationThryvId] ||
                    location.locationThryvId}{' '}
                  <span style={{fontWeight: 400, color: '#6a7181'}}>
                    - {itemCount} {itemCount === 1 ? 'item' : 'items'}
                  </span>
                </span>
              </div>
              <span style={{...categoryLabelStyle, fontSize: '13px'}}>
                {formatPrice({amount: total, country})}
              </span>
            </div>
            {isOpen && (
              <KCPPaymentDetails
                country={country}
                paymentState={paymentState}
                paymentDetailType={paymentDetailType}
                cartDetailObj={location.cart}
                currentScreen={currentScreen}
                couponList={locationCoupons}
                preselectedSubData={preselectedSubData}
                hideHeading
                isTaxCalculated={isTaxCalculated}
                isLocationSection
                showLocationCoupons={Array.isArray(location.coupons)}
                previewDetails={
                  location.preview
                    ? {
                        paymentPreview: location.preview,
                        ...(Array.isArray(location.coupons) && {
                          coupons: locationCoupons,
                        }),
                      }
                    : undefined
                }
              />
            )}
          </div>
        )
      })}
    </div>
  )

  const rankItem = item => {
    const ranking = {
      'addon-socialadsbronze-5-m2m': 2,
      'addon-socialadssilver-5-m2m': 3,
      'addon-socialadsgold-5-m2m': 4,
    }

    const identifiers = [item.code, item.planCode, item.addonCode].filter(
      Boolean,
    )

    const matched = identifiers.find(id => ranking[id])

    return matched ? ranking[matched] : 1
  }

  const itemComparator = (a, b) => {
    const rankA = rankItem(a)
    const rankB = rankItem(b)
    return rankA - rankB
  }

  return (
    <div style={{backgroundColor: 'transparent'}}>
      {previewDetails?.paymentPreview && !isMultiLocation ? (
        <>
          {!hideHeading &&
            (isMobileViewEnable ? (
              <p
                style={{
                  fontFamily: 'Open Sans',
                  fontWeight: 600,
                  fontStyle: 'SemiBold',
                  fontSize: '20px',
                  lineHeight: '0px',
                  letterSpacing: 0,
                  marginBottom: '32px',
                  color: '#000000',
                }}
              >
                Due{' '}
                {/* {paymentDetailType === 'monthly' ? recurringCycleLabel : 'Today'} */}
                {paymentDetailType === 'monthly' ? 'Monthly' : 'Today'}
              </p>
            ) : (
              <h2
                style={{
                  fontFamily: 'Open Sans',
                  fontWeight: 600,
                  fontStyle: 'SemiBold',
                  fontSize: '20px',
                  lineHeight: '44px',
                  letterSpacing: 0,
                  marginBottom: '12px',
                  color: '#000000',
                }}
              >
                Due{' '}
                {/* {paymentDetailType === 'monthly' ? recurringCycleLabel : 'Today'} */}
                {paymentDetailType === 'monthly' ? 'Monthly' : 'Today'}
              </h2>
            ))}

          {renderPlanDetails(previewDetails.paymentPreview)}
          {paymentDetailType === 'today' &&
            previewDetails.coupons &&
            previewDetails.coupons.length > 0 &&
            (() => {
              return (
                <div
                  style={{
                    background: '#F1F5FF',
                    borderRadius: '10px',
                    padding: '10px',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '5px',
                    marginTop: '8px',
                  }}
                >
                  <div
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      padding: '0 10px',
                      cursor: 'pointer',
                      gap: '5px',
                    }}
                    onClick={() => setIncentivesOpen(v => !v)}
                  >
                    <div style={{display: 'flex', alignItems: 'center'}}>
                      <span
                        style={{
                          display: 'inline-flex',
                          width: '12px',
                          height: '12px',
                          flexShrink: 0,
                          transform: incentivesOpen
                            ? 'rotate(180deg)'
                            : 'rotate(0deg)',
                          transition: 'transform 0.2s ease',
                          marginLeft: '6px',
                          marginRight: '8px',
                        }}
                      >
                        <svg
                          width="12"
                          height="12"
                          viewBox="0 0 12 12"
                          fill="none"
                          style={{width: '100%', height: '100%'}}
                        >
                          <path
                            d="M6.18568 8.49999C6.10703 8.50047 6.02907 8.48471 5.95626 8.45362C5.88345 8.42253 5.81722 8.37673 5.76138 8.31883L2.17575 4.57076C2.06322 4.45313 2 4.29359 2 4.12724C2 3.96088 2.06322 3.80134 2.17575 3.68371C2.28828 3.56608 2.44091 3.5 2.60005 3.5C2.75919 3.5 2.91182 3.56608 3.02435 3.68371L6.18568 6.99451L9.34701 3.68996C9.46133 3.58762 9.60839 3.53414 9.75879 3.54022C9.9092 3.54629 10.0519 3.61146 10.1583 3.72272C10.2647 3.83397 10.3271 3.9831 10.3329 4.14032C10.3387 4.29754 10.2875 4.45126 10.1896 4.57076L6.604 8.31883C6.49269 8.43424 6.34246 8.4993 6.18568 8.49999Z"
                            fill="currentColor"
                          />
                        </svg>
                      </span>
                      <span
                        style={{
                          fontSize: '12px',
                          fontWeight: 700,
                          fontFamily: 'Open Sans',
                          color: '#121212',
                        }}
                      >
                        Coupons & Discounts{' '}
                        <span style={{fontWeight: 400}}>
                          - {previewDetails.coupons.length} item
                          {previewDetails.coupons.length !== 1 ? 's' : ''}
                        </span>
                      </span>
                    </div>
                    {(previewDetails.paymentPreview?.discount ||
                      overAllDeduction) > 0 && (
                      <span
                        style={{
                          fontSize: '12px',
                          fontWeight: 700,
                          fontFamily: 'Open Sans',
                          color: '#121212',
                        }}
                      >
                        -
                        {formatPrice({
                          amount:
                            previewDetails.paymentPreview?.discount ||
                            overAllDeduction,
                          country,
                        })}
                      </span>
                    )}
                  </div>
                  {incentivesOpen && (
                    <div
                      style={{
                        borderRadius: '6px',
                        overflow: 'hidden',
                        display: 'flex',
                        flexDirection: 'column',
                        gap: '5px',
                      }}
                    >
                      {previewDetails.coupons.map(item => {
                        const isPercent = item.type === 'percent'
                        return (
                          <div
                            key={item?.name}
                            style={{
                              position: 'relative',
                              display: 'flex',
                              alignItems: 'center',
                              gap: '10px',
                              background: '#FFF9F6',
                              border: '1.5px solid #FF5000',
                              overflow: 'hidden',
                              borderRadius: '6px',
                              padding: '10px',
                            }}
                          >
                            <div
                              style={{
                                position: 'absolute',
                                left: 0,
                                top: '50%',
                                transform: 'translateY(-50%)',
                                width: '3px',
                                height: '25px',
                                background: '#FF5000',
                                borderRadius: '0 10px 10px 0',
                              }}
                            />
                            <div style={{flex: 1}}>
                              <p
                                style={{
                                  fontFamily: 'Open Sans',
                                  fontWeight: 700,
                                  fontSize: '12px',
                                  margin: '0px',
                                  color: '#121212',
                                }}
                              >
                                {item.name}
                              </p>
                              <p
                                style={{
                                  fontFamily: 'Open Sans',
                                  fontWeight: 400,
                                  fontSize: '11px',
                                  margin: '0px',
                                  color: '#6a7181',
                                }}
                              >
                                {isPercent
                                  ? 'Discount Applied'
                                  : 'Coupon Applied'}
                              </p>
                            </div>
                          </div>
                        )
                      })}
                    </div>
                  )}
                </div>
              )
            })()}
          {paymentDetailType !== 'monthly' &&
            !isAusNz &&
            !isLocationSection && (
              <div
                style={{
                  position: 'relative',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '10px',
                  background: '#FFF9F6',
                  border: '1.5px solid #FF5000',
                  overflow: 'hidden',
                  borderRadius: '6px',
                  padding: '10px',
                  marginTop: '8px',
                }}
              >
                <div
                  style={{
                    position: 'absolute',
                    left: 0,
                    top: '50%',
                    transform: 'translateY(-50%)',
                    width: '3px',
                    height: '25px',
                    background: '#FF5000',
                    borderRadius: '0 10px 10px 0',
                  }}
                />
                <div style={{flex: 1}}>
                  <p
                    style={{
                      fontFamily: 'Open Sans',
                      fontWeight: 700,
                      fontSize: '12px',
                      margin: '0px',
                      color: '#121212',
                    }}
                  >
                    Tax &amp; Fees
                  </p>
                </div>
                <div style={{flexShrink: 0, textAlign: 'right'}}>
                  <p
                    style={{
                      fontFamily: 'Open Sans',
                      fontWeight: 400,
                      fontSize: '12px',
                      margin: '0px',
                      color: '#121212',
                    }}
                  >
                    {calculateTax()}
                  </p>
                </div>
              </div>
            )}
          {isAus && !isLocationSection && paymentDetailType !== 'today' && (
            <ParagraphText
              variant="sm"
              style={{
                opacity: '50%',
                marginTop: '10px',
                fontSize: 'clamp(11px, 1.2vw, 13px)',
              }}
            >
              {previewDetails?.paymentPreview
                ? '*All plans include GST'
                : '*All plans include GST except SEO'}
            </ParagraphText>
          )}
          {paymentDetailType === 'today' && isAu && !isLocationSection && (
            <ParagraphText
              variant="sm"
              style={{
                opacity: '50%',
                marginTop: '10px',
                fontSize: 'clamp(11px, 1.2vw, 13px)',
              }}
            >
              *All plans include GST
            </ParagraphText>
          )}
        </>
      ) : (
        <>
          {!hideHeading &&
            (isMobileViewEnable ? (
              <p
                style={{
                  fontFamily: 'Open Sans',
                  fontWeight: 600,
                  fontStyle: 'SemiBold',
                  fontSize: '20px',
                  lineHeight: '0px',
                  letterSpacing: 0,
                  marginBottom: '32px',
                  color: '#000000',
                }}
              >
                Due {paymentDetailType === 'monthly' ? 'Monthly' : 'Today'}
              </p>
            ) : (
              <h2
                style={{
                  fontFamily: 'Open Sans',
                  fontWeight: 600,
                  fontStyle: 'SemiBold',
                  fontSize: '20px',
                  lineHeight: '44px',
                  letterSpacing: 0,
                  marginBottom: '12px',
                  color: '#000000',
                }}
              >
                Due {paymentDetailType === 'monthly' ? 'Monthly' : 'Today'}
              </h2>
            ))}

          {isMultiLocation
            ? renderMultiLocation()
            : subList && subList.length > 0 && renderGroupedFallback()}
          {paymentDetailType === 'today' &&
            (isLocationSection ? showLocationCoupons : !hasLocationCoupons) &&
            couponList &&
            couponList.length > 0 && (
              <div
                style={{
                  background: '#F1F5FF',
                  borderRadius: '10px',
                  padding: '10px',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '5px',
                  marginTop: '8px',
                }}
              >
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    padding: '0 10px',
                    cursor: 'pointer',
                    gap: '5px',
                  }}
                  onClick={() => setIncentivesOpen(v => !v)}
                >
                  <div style={{display: 'flex', alignItems: 'center'}}>
                    <span
                      style={{
                        display: 'inline-flex',
                        width: '12px',
                        height: '12px',
                        flexShrink: 0,
                        transform: incentivesOpen
                          ? 'rotate(180deg)'
                          : 'rotate(0deg)',
                        transition: 'transform 0.2s ease',
                        marginLeft: '6px',
                        marginRight: '8px',
                      }}
                    >
                      <svg
                        width="12"
                        height="12"
                        viewBox="0 0 12 12"
                        fill="none"
                        style={{width: '100%', height: '100%'}}
                      >
                        <path
                          d="M6.18568 8.49999C6.10703 8.50047 6.02907 8.48471 5.95626 8.45362C5.88345 8.42253 5.81722 8.37673 5.76138 8.31883L2.17575 4.57076C2.06322 4.45313 2 4.29359 2 4.12724C2 3.96088 2.06322 3.80134 2.17575 3.68371C2.28828 3.56608 2.44091 3.5 2.60005 3.5C2.75919 3.5 2.91182 3.56608 3.02435 3.68371L6.18568 6.99451L9.34701 3.68996C9.46133 3.58762 9.60839 3.53414 9.75879 3.54022C9.9092 3.54629 10.0519 3.61146 10.1583 3.72272C10.2647 3.83397 10.3271 3.9831 10.3329 4.14032C10.3387 4.29754 10.2875 4.45126 10.1896 4.57076L6.604 8.31883C6.49269 8.43424 6.34246 8.4993 6.18568 8.49999Z"
                          fill="currentColor"
                        />
                      </svg>
                    </span>
                    <span
                      style={{
                        fontSize: '12px',
                        fontWeight: 700,
                        fontFamily: 'Open Sans',
                        color: '#121212',
                      }}
                    >
                      Coupons & Discounts{' '}
                      <span style={{fontWeight: 400}}>
                        - {couponList.length} item
                        {couponList.length !== 1 ? 's' : ''}
                      </span>
                    </span>
                  </div>
                  {overAllDeduction > 0 && (
                    <span
                      style={{
                        fontSize: '12px',
                        fontWeight: 700,
                        fontFamily: 'Open Sans',
                        color: '#121212',
                      }}
                    >
                      -{formatPrice({amount: overAllDeduction, country})}
                    </span>
                  )}
                </div>
                {incentivesOpen && (
                  <div
                    style={{
                      borderRadius: '6px',
                      overflow: 'hidden',
                      display: 'flex',
                      flexDirection: 'column',
                      gap: '5px',
                    }}
                  >
                    {couponList.map(item => {
                      const isPercent = item.type === 'percent'
                      return (
                        <div
                          key={item?.name}
                          style={{
                            position: 'relative',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '10px',
                            background: '#FFF9F6',
                            border: '1.5px solid #FF5000',
                            overflow: 'hidden',
                            borderRadius: '6px',
                            padding: '10px',
                          }}
                        >
                          <div
                            style={{
                              position: 'absolute',
                              left: 0,
                              top: '50%',
                              transform: 'translateY(-50%)',
                              width: '3px',
                              height: '25px',
                              background: '#FF5000',
                              borderRadius: '0 10px 10px 0',
                            }}
                          />
                          <div style={{flex: 1}}>
                            <p
                              style={{
                                fontFamily: 'Open Sans',
                                fontWeight: 700,
                                fontSize: '12px',
                                margin: '0px',
                                color: '#121212',
                              }}
                            >
                              {item.name}
                            </p>
                            <p
                              style={{
                                fontFamily: 'Open Sans',
                                fontWeight: 400,
                                fontSize: '11px',
                                margin: '0px',
                                color: '#6a7181',
                              }}
                            >
                              {isPercent
                                ? 'Discount Applied'
                                : 'Coupon Applied'}
                            </p>
                          </div>
                        </div>
                      )
                    })}
                  </div>
                )}
              </div>
            )}
          {paymentDetailType !== 'monthly' &&
            !isAusNz &&
            !isLocationSection && (
              <div
                style={{
                  position: 'relative',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '10px',
                  background: '#FFF9F6',
                  border: '1.5px solid #FF5000',
                  overflow: 'hidden',
                  borderRadius: '6px',
                  padding: '10px',
                  marginTop: '8px',
                }}
              >
                <div
                  style={{
                    position: 'absolute',
                    left: 0,
                    top: '50%',
                    transform: 'translateY(-50%)',
                    width: '3px',
                    height: '25px',
                    background: '#FF5000',
                    borderRadius: '0 10px 10px 0',
                  }}
                />
                <div style={{flex: 1}}>
                  <p
                    style={{
                      fontFamily: 'Open Sans',
                      fontWeight: 700,
                      fontSize: '12px',
                      margin: '0px',
                      color: '#121212',
                    }}
                  >
                    Tax &amp; Fees
                  </p>
                </div>
                <div style={{flexShrink: 0, textAlign: 'right'}}>
                  <p
                    style={{
                      fontFamily: 'Open Sans',
                      fontWeight: 400,
                      fontSize: '12px',
                      margin: '0px',
                      color: '#121212',
                    }}
                  >
                    {calculateTax()}
                  </p>
                </div>
              </div>
            )}
          {isAus && !isLocationSection && paymentDetailType !== 'today' && (
            <ParagraphText
              variant="sm"
              style={{
                opacity: '50%',
                marginTop: '10px',
                fontSize: 'clamp(11px, 1.2vw, 13px)',
              }}
            >
              {previewDetails?.paymentPreview
                ? '*All plans include GST'
                : '*All plans include GST except SEO'}
            </ParagraphText>
          )}
          {paymentDetailType === 'today' && isAu && !isLocationSection && (
            <ParagraphText
              variant="sm"
              style={{
                opacity: '50%',
                marginTop: '10px',
                fontSize: 'clamp(11px, 1.2vw, 13px)',
              }}
            >
              *All plans include GST
            </ParagraphText>
          )}
        </>
      )}
    </div>
  )
}
